"use client";

/**
 * The viewer's theme choice: light, dark, or whatever the operating system
 * uses. Kept in localStorage (it is a per-browser preference), shared by every
 * component through useSyncExternalStore, and followed across tabs.
 */

import { useSyncExternalStore } from "react";
import type { ResolvedTheme } from "./antd-theme";
import {
	DARK_QUERY,
	DEFAULT_THEME_MODE,
	THEME_MODES,
	THEME_STORAGE_KEY,
	type ThemeMode,
} from "./theme-config";

export type { ThemeMode };

const isMode = (value: unknown): value is ThemeMode =>
	THEME_MODES.includes(value as ThemeMode);

const listeners = new Set<() => void>();
// used when storage is unavailable (private mode, blocked site data)
let memoryMode: ThemeMode | null = null;

const readMode = (): ThemeMode => {
	try {
		const stored = localStorage.getItem(THEME_STORAGE_KEY);
		if (isMode(stored)) return stored;
	} catch {
		// fall through
	}
	return memoryMode ?? DEFAULT_THEME_MODE;
};

const subscribeMode = (onChange: () => void) => {
	listeners.add(onChange);
	// a choice made in another tab
	const onStorage = (e: StorageEvent) => {
		if (e.key === THEME_STORAGE_KEY) onChange();
	};
	window.addEventListener("storage", onStorage);
	return () => {
		listeners.delete(onChange);
		window.removeEventListener("storage", onStorage);
	};
};

const subscribeSystem = (onChange: () => void) => {
	const query = window.matchMedia(DARK_QUERY);
	query.addEventListener("change", onChange);
	return () => query.removeEventListener("change", onChange);
};

const systemIsDark = () => window.matchMedia(DARK_QUERY).matches;

export const resolveTheme = (
	mode: ThemeMode,
	systemDark: boolean,
): ResolvedTheme =>
	mode === "dark" || (mode === "system" && systemDark) ? "dark" : "light";

/** the theme the stored choice asks for right now (outside React) */
export const wantedTheme = (): ResolvedTheme =>
	resolveTheme(readMode(), systemIsDark());

export const setThemeMode = (mode: ThemeMode) => {
	memoryMode = mode;
	try {
		localStorage.setItem(THEME_STORAGE_KEY, mode);
	} catch {
		// kept in memory for this page
	}
	listeners.forEach((listener) => listener());
};

/**
 * The current choice and the theme it resolves to. The prerendered HTML is
 * light, so hydration renders light (the server snapshots) and React switches
 * to the stored choice straight after.
 */
export const useThemeMode = () => {
	const mode = useSyncExternalStore(
		subscribeMode,
		readMode,
		() => DEFAULT_THEME_MODE,
	);
	const systemDark = useSyncExternalStore(
		subscribeSystem,
		systemIsDark,
		() => false,
	);
	return {
		mode,
		resolved: resolveTheme(mode, systemDark),
		setMode: setThemeMode,
	};
};
